/**
 * MentionInput - Input with @mention autocomplete
 *
 * Maintains display string (what user sees: "@Alice") and
 * serialized string (stored: "@[Alice](abc123)").
 * Shows dropdown when @ is typed, filtered by text after @.
 */
import { useState, useRef, useEffect, useCallback, useImperativeHandle, type KeyboardEvent, type Ref } from 'react';
import type React from 'react';
import { Input } from '@/components/ui/input';

interface MentionMember {
  userId: string;
  name: string;
}

interface MentionInputProps {
  value: string;
  onChange: (serialized: string) => void;
  members: MentionMember[];
  placeholder?: string;
  maxLength?: number;
  onSubmit?: () => void;
  disabled?: boolean;
}

interface MentionSpan {
  displayStart: number;
  displayEnd: number;
  name: string;
  userId: string;
}

export function MentionInput({ value, onChange, members, placeholder, maxLength, onSubmit, disabled, ref }: MentionInputProps & { ref?: Ref<HTMLInputElement> }) {
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current!);

  // Track mention spans in the display string
  const [mentions, setMentions] = useState<MentionSpan[]>([]);
  const [displayValue, setDisplayValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [atPosition, setAtPosition] = useState(-1);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Parse initial serialized value into display + mentions
  useEffect(() => {
    if (!value) {
      setDisplayValue('');
      setMentions([]);
      return;
    }
    const spans: MentionSpan[] = [];
    let display = '';
    let lastIndex = 0;
    const regex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    for (const match of value.matchAll(regex)) {
      const start = match.index!;
      display += value.slice(lastIndex, start);
      const displayStart = display.length;
      const name = match[1]!;
      display += `@${name}`;
      spans.push({ displayStart, displayEnd: display.length, name, userId: match[2]! });
      lastIndex = start + match[0].length;
    }
    display += value.slice(lastIndex);
    setDisplayValue(display);
    setMentions(spans);
  }, []); // Only on mount

  const buildSerialized = useCallback((display: string, spans: MentionSpan[]): string => {
    let result = '';
    let lastIdx = 0;
    const sorted = [...spans].sort((a, b) => a.displayStart - b.displayStart);
    for (const span of sorted) {
      result += display.slice(lastIdx, span.displayStart);
      result += `@[${span.name}](${span.userId})`;
      lastIdx = span.displayEnd;
    }
    result += display.slice(lastIdx);
    return result;
  }, []);

  const filteredMembers = members.filter(m =>
    m.name.toLowerCase().includes(filterText.toLowerCase())
  ).slice(0, 5);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDisplay = e.target.value;
    const cursorPos = e.target.selectionStart ?? newDisplay.length;

    // Update mentions — remove any that overlap with the changed region
    const delta = newDisplay.length - displayValue.length;
    const changeStart = cursorPos - Math.max(0, delta);
    const updatedMentions = mentions
      .filter(m => {
        // Remove mentions that were edited
        if (changeStart >= m.displayStart && changeStart <= m.displayEnd) return false;
        if (cursorPos >= m.displayStart && cursorPos <= m.displayEnd) return false;
        return true;
      })
      .map(m => {
        if (m.displayStart >= changeStart) {
          return { ...m, displayStart: m.displayStart + delta, displayEnd: m.displayEnd + delta };
        }
        return m;
      });

    setDisplayValue(newDisplay);
    setMentions(updatedMentions);

    // Check for @ trigger
    const textBeforeCursor = newDisplay.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    if (atMatch) {
      setShowDropdown(true);
      setFilterText(atMatch[1] || '');
      setAtPosition(cursorPos - atMatch[0].length);
      setSelectedIndex(0);
    } else {
      setShowDropdown(false);
    }

    onChange(buildSerialized(newDisplay, updatedMentions));
  };

  const selectMember = useCallback((member: MentionMember) => {
    const beforeAt = displayValue.slice(0, atPosition);
    const afterCursor = displayValue.slice(atPosition + 1 + filterText.length);
    const mentionDisplay = `@${member.name}`;
    const newDisplay = beforeAt + mentionDisplay + ' ' + afterCursor;

    const newSpan: MentionSpan = {
      displayStart: atPosition,
      displayEnd: atPosition + mentionDisplay.length,
      name: member.name,
      userId: member.userId,
    };

    // Shift existing mentions after insertion point
    const insertLen = mentionDisplay.length + 1 - (1 + filterText.length); // +1 for space
    const updatedMentions = mentions
      .map(m => {
        if (m.displayStart >= atPosition) {
          return { ...m, displayStart: m.displayStart + insertLen, displayEnd: m.displayEnd + insertLen };
        }
        return m;
      });

    const allMentions = [...updatedMentions, newSpan];
    setDisplayValue(newDisplay);
    setMentions(allMentions);
    setShowDropdown(false);
    onChange(buildSerialized(newDisplay, allMentions));

    // Re-focus input
    setTimeout(() => {
      const pos = atPosition + mentionDisplay.length + 1;
      inputRef.current?.setSelectionRange(pos, pos);
      inputRef.current?.focus();
    }, 0);
  }, [displayValue, atPosition, filterText, mentions, onChange, buildSerialized]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (showDropdown && filteredMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, filteredMembers.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        selectMember(filteredMembers[selectedIndex]!);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowDropdown(false);
        return;
      }
    } else if (e.key === 'Enter' && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  };

  // Close dropdown on click outside
  useEffect(() => {
    if (!showDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (!dropdownRef.current?.contains(e.target as Node) && e.target !== inputRef.current) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDropdown]);

  return (
    <div className="relative flex-1">
      <Input
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        className="rounded-full"
      />
      {showDropdown && filteredMembers.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute bottom-full mb-1 left-0 w-full max-h-40 overflow-y-auto rounded-xl border bg-background shadow-lg z-50"
        >
          {filteredMembers.map((member, i) => (
            <button
              key={member.userId}
              onClick={() => selectMember(member)}
              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                i === selectedIndex ? 'bg-accent text-foreground' : 'text-foreground hover:bg-muted'
              }`}
            >
              {member.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
